import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, Trash2 } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type AISettings = {
  aiEnabled: boolean;
  aiOverageCapCents: number;
  aiCatchMeUpEnabled: boolean;
  monthGiftedAllowanceCents: number;
  monthUsageCents: number;
  paidRemainingCents: number;
  // Wave 9 — image generation controls
  aiImageEnabled: boolean;
  aiImageNanoBananaEnabled: boolean;
  aiImageRunwayEnabled: boolean;
  aiImageMaxPerUserPerDay: number | null;
  // Task #601 — AI Reply Management toggles
  aiReplyManagementEnabled: boolean;
  aiReplyCategorizationEnabled: boolean;
  aiReplyDraftEnabled: boolean;
  // Task #25 — AI Life Coach workspace master toggle
  aiCoachEnabled: boolean;
  // Task #52 — Lead Gen result cap (admin-only)
  leadGenAiResultCapEnabled: boolean;
  leadGenAiResultCap: number | null;
  // AI Search per-workspace enable toggle (global feature settings live in Platform Config)
  leadGenAiEnabled: boolean;
};

type AIUsageBreakdown = {
  features: { feature: string; costCents: number; calls: number }[];
};

type LeadCatcherStats = {
  today: { conversations: number; costCents: number };
  week: {
    conversations: number;
    costCents: number;
    avgTokensPerConversation: number;
    avgCostPerConversationCents: number;
    conversationsPerDay: number;
  };
  daily: { day: string; conversations: number; costCents: number }[];
};

function fmt(c: number) {
  return `$${(c / 100).toFixed(2)}`;
}

export function AISettingsCard() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<AISettings>({ queryKey: ["/api/admin/ai-settings"] });

  const [enabled, setEnabled] = useState(true);
  const [catchMeUpEnabled, setCatchMeUpEnabled] = useState(true);
  // Task #25 — AI Life Coach workspace master toggle.
  const [coachEnabled, setCoachEnabled] = useState(true);
  // Task #601 — AI Reply Management toggles. Master + 2 sub-toggles.
  const [replyMgmtEnabled, setReplyMgmtEnabled] = useState(true);
  const [replyCategorizeEnabled, setReplyCategorizeEnabled] = useState(true);
  const [replyDraftEnabled, setReplyDraftEnabled] = useState(true);
  // Task #52 — Lead Gen result cap (admin-only).
  const [capEnabled, setCapEnabled] = useState(false);
  const [capValue, setCapValue] = useState("50");
  // AI Search per-workspace enable toggle.
  const [leadGenAiEnabled, setLeadGenAiEnabled] = useState(true);

  useEffect(() => {
    if (data) {
      setEnabled(!!data.aiEnabled);
      setCatchMeUpEnabled(data.aiCatchMeUpEnabled !== false);
      setCoachEnabled(data.aiCoachEnabled !== false);
      setReplyMgmtEnabled(data.aiReplyManagementEnabled !== false);
      setReplyCategorizeEnabled(data.aiReplyCategorizationEnabled !== false);
      setReplyDraftEnabled(data.aiReplyDraftEnabled !== false);
      setCapEnabled(!!data.leadGenAiResultCapEnabled);
      if (data.leadGenAiResultCap !== null && data.leadGenAiResultCap !== undefined) {
        setCapValue(String(data.leadGenAiResultCap));
      }
      setLeadGenAiEnabled(data.leadGenAiEnabled !== false);
    }
  }, [data]);

  // On-demand prune of the "Catch me up" summary cache. The scheduler
  // already runs this every 6 hours, so the typical case is "I just
  // bulk-deleted leads and want the cache rows gone now" — we surface the
  // returned counts in a toast so admins can confirm something happened.
  const cleanupCache = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/ai-catch-me-up-cache/cleanup", {});
      return (await res.json()) as {
        ok: boolean;
        orphanedLeads: number;
        orphanedClients: number;
        expired: number;
        totalPruned: number;
      };
    },
    onSuccess: (r) => {
      toast({
        title: r.totalPruned === 0 ? "Cache already clean" : `Pruned ${r.totalPruned} cache row${r.totalPruned === 1 ? "" : "s"}`,
        description:
          r.totalPruned === 0
            ? "No orphaned or expired summaries to remove."
            : `${r.orphanedLeads} orphaned leads, ${r.orphanedClients} orphaned clients, ${r.expired} expired.`,
      });
    },
    onError: (e: any) =>
      toast({ title: "Cache cleanup failed", description: e?.message, variant: "destructive" }),
  });

  const save = useMutation({
    mutationFn: async () =>
      apiRequest("PATCH", "/api/admin/ai-settings", {
        aiEnabled: enabled,
        aiCatchMeUpEnabled: catchMeUpEnabled,
        aiCoachEnabled: coachEnabled,
        aiReplyManagementEnabled: replyMgmtEnabled,
        aiReplyCategorizationEnabled: replyCategorizeEnabled,
        aiReplyDraftEnabled: replyDraftEnabled,
        leadGenAiResultCapEnabled: capEnabled,
        leadGenAiResultCap: capEnabled ? parseInt(capValue, 10) : null,
        leadGenAiEnabled,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ai-settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ai/balance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/social/ai-images/policy"] });
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/settings"] });
      toast({ title: "AI settings saved" });
    },
    onError: (e: any) => toast({ title: "Failed", description: e?.message, variant: "destructive" }),
  });

  return (
    <Card data-testid="card-admin-ai-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" /> AI Settings
        </CardTitle>
        <CardDescription>
          Toggle AI drafting on or off, and set a hard monthly overage cap so spend can't surprise you.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading || !data ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <div className="text-sm font-medium">"Catch me up" summaries</div>
                <div className="text-xs text-muted-foreground">
                  Lets reps summarize a lead or client's full activity history into 3 bullets.
                  1 credit per click; cached and reused for free until a new activity is logged.
                </div>
              </div>
              <Switch
                checked={catchMeUpEnabled}
                onCheckedChange={setCatchMeUpEnabled}
                disabled={!enabled}
                data-testid="switch-ai-catch-me-up"
              />
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <div className="text-sm font-medium">AI Life Coach</div>
                <div className="text-xs text-muted-foreground">
                  Lets users opt in to a personal life-coach layer in the voice assistant.
                  When off, no user in this workspace can enable it.
                </div>
              </div>
              <Switch
                checked={coachEnabled}
                onCheckedChange={setCoachEnabled}
                disabled={!enabled}
                data-testid="switch-ai-coach-enabled"
              />
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <div className="text-sm font-medium">Summary cache cleanup</div>
                <div className="text-xs text-muted-foreground">
                  Runs automatically every 6 hours. Use this to immediately drop cached summaries for deleted leads or clients.
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => cleanupCache.mutate()}
                disabled={cleanupCache.isPending}
                data-testid="button-ai-cache-cleanup"
              >
                {cleanupCache.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-1.5" />
                )}
                Run cleanup now
              </Button>
            </div>

            {/* ── AI Search per-workspace toggle ────────────────────────── */}
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <div className="text-sm font-medium">AI Search enabled</div>
                <div className="text-xs text-muted-foreground">
                  Show the AI Search tab on the Lead Gen page for this workspace.
                  Global feature settings (unverified results, email finder, Apollo, recurring) are configured
                  in Platform Config → AI Lead Search.
                </div>
              </div>
              <Switch
                checked={leadGenAiEnabled}
                onCheckedChange={setLeadGenAiEnabled}
                data-testid="switch-lead-gen-ai-enabled"
              />
            </div>

            {/* ── Task #52 — Lead Gen result cap ───────────────────────── */}
            <div className="space-y-2 rounded-md border p-3" data-testid="section-lead-gen-result-cap">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Cap results per search</div>
                  <div className="text-xs text-muted-foreground">
                    Sets a hard ceiling on candidates Claude returns per AI search. Lower = cheaper.
                  </div>
                </div>
                <Switch
                  checked={capEnabled}
                  onCheckedChange={setCapEnabled}
                  data-testid="switch-lead-gen-result-cap-enabled"
                />
              </div>
              {capEnabled && (
                <div className="pt-1 flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="200"
                    value={capValue}
                    onChange={(e) => setCapValue(e.target.value)}
                    className="max-w-[120px]"
                    data-testid="input-lead-gen-result-cap"
                  />
                  <span className="text-xs text-muted-foreground">max results per search (1–200)</span>
                </div>
              )}
            </div>

            {/* ── Task #601 — AI Reply Management ───────────────────────── */}
            <div className="space-y-3 rounded-md border p-3" data-testid="section-ai-reply-management">
              <div className="text-sm font-medium">AI Reply Management</div>
              <div className="text-xs text-muted-foreground -mt-2">
                Auto-categorizes new inbound emails (logistics / negotiation /
                objection / scheduling / etc.) and drafts a suggested reply that
                reps approve with one click. The actual send still requires a
                rep to click Send — nothing goes out automatically.
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <div className="text-sm font-medium">AI assist enabled</div>
                  <div className="text-xs text-muted-foreground">Off blocks every Draft-with-AI button.</div>
                </div>
                <Switch
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  data-testid="switch-ai-enabled"
                />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <div className="text-sm font-medium">Reply Management enabled</div>
                  <div className="text-xs text-muted-foreground">Master switch for both categorize and draft.</div>
                </div>
                <Switch
                  checked={replyMgmtEnabled}
                  onCheckedChange={setReplyMgmtEnabled}
                  disabled={!enabled}
                  data-testid="switch-ai-reply-management"
                />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <div className="text-sm font-medium">Auto-categorize inbound</div>
                  <div className="text-xs text-muted-foreground">
                    Free — platform-absorbed. Categories appear instantly on every inbound email.
                  </div>
                </div>
                <Switch
                  checked={replyCategorizeEnabled}
                  onCheckedChange={setReplyCategorizeEnabled}
                  disabled={!enabled || !replyMgmtEnabled}
                  data-testid="switch-ai-reply-categorize"
                />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <div className="text-sm font-medium">Auto-draft replies</div>
                  <div className="text-xs text-muted-foreground">
                    1 credit per draft. Generated on demand — click "Generate draft" in the inbox.
                  </div>
                </div>
                <Switch
                  checked={replyDraftEnabled}
                  onCheckedChange={setReplyDraftEnabled}
                  disabled={!enabled || !replyMgmtEnabled}
                  data-testid="switch-ai-reply-draft"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="button-save-ai-settings">
                {save.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                Save AI settings
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
